import React, { useState } from 'react';
import { 
  FileText,
  Plus,
  Edit,
  Power,
  PowerOff,
  Trash2,
  ChevronDown,
  ChevronUp,
  Calendar,
  Layers,
  Bell
} from 'lucide-react';

const FeeStructureItem = ({ structure, allClasses, formatCurrency, onEdit, onDelete, onSendReminders, sendingReminders }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const applicableClasses = structure.applicableClassIds || [];
  const classNames = applicableClasses.map(cId => allClasses.find(c => c._id === cId)?.unitName).filter(Boolean);
  const totalAmount = structure.categories?.reduce((sum, cat) => sum + (cat.amount || 0), 0) || 0;
  const isSending = sendingReminders === structure._id;

  return (
    <div className="bg-white border border-gray-200 rounded-lg hover:border-primary-300 transition-all duration-200 overflow-hidden">
      {/* Header / Summary View */}
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        className="p-3 flex items-center justify-between cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3 flex-1">
          <div className={`p-1.5 rounded bg-green-100`}>
            <FileText className={`h-4 w-4 text-green-600`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-gray-900">{structure.name}</h3>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3"/> {structure.academicYear}</span>
              <span className="flex items-center gap-1"><Layers className="h-3 w-3"/> {structure.categories?.length || 0} Categories</span>
              <span className="font-bold text-gray-900">Total: {formatCurrency(totalAmount)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
           <div className="text-right hidden sm:block">
              <div className="text-[10px] text-gray-500">Applicable to</div>
              <div className="text-xs font-medium text-gray-900">{classNames.length} Classes</div>
           </div>
           {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="p-3 border-t border-gray-200 bg-white">
          {structure.description && (
            <div className="mb-3 text-xs text-gray-600 bg-gray-50 p-2 rounded">
              {structure.description}
            </div>
          )}

          {classNames.length > 0 && (
            <div className="mb-3">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Applicable Classes</span>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {classNames.map((name, idx) => (
                  <span key={idx} className="px-1.5 py-0.5 text-[10px] rounded border" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', color: 'var(--primary-color, #4f46e5)', borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 20%, white)' }}>
                    {name}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mb-3">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Fee Breakdown</span>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-1.5">
              {structure.categories?.map((cat, idx) => (
                <div key={idx} className="bg-white border border-gray-200 rounded p-2 text-xs shadow-sm">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-medium text-gray-900">{cat.name}</span>
                    <span className="font-bold" style={{ color: 'var(--primary-color, #4f46e5)' }}>{formatCurrency(cat.amount)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-gray-500">
                    <span>Due: {cat.dueDate ? new Date(cat.dueDate).toLocaleDateString() : 'N/A'}</span>
                    {cat.isMandatory && <span className="text-yellow-600 font-medium">Mandatory</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              onClick={(e) => { e.stopPropagation(); onSendReminders(structure._id); }}
              disabled={isSending}
              className="px-2 py-1 text-xs font-medium text-yellow-600 hover:bg-yellow-50 rounded transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Send fee reminders to students with pending balance"
            >
              <Bell className="h-3.5 w-3.5" /> {isSending ? 'Sending...' : 'Reminders'}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(structure); }}
              className="px-2 py-1 text-xs font-medium rounded transition-colors flex items-center gap-1"
              style={{ color: 'var(--primary-color, #4f46e5)' }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = ''}
            >
              <Edit className="h-3.5 w-3.5" /> Edit
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); onDelete(structure._id); }}
              className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors flex items-center gap-1"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const FeeStructureList = ({ 
  structures, 
  loading, 
  allClasses, 
  formatCurrency, 
  onEdit, 

  onDelete,
  onCreate,
  onSendReminders,
  sendingReminders
}) => {
  return (
    <div className="bg-white p-3 rounded shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="p-1.5 bg-purple-100 rounded"><FileText className="h-4 w-4 text-purple-600"/></div>
          <div className="ml-2">
            <h2 className="text-sm font-bold text-gray-900">Fee Structures</h2>
            <p className="text-xs text-gray-500">Manage fee templates and assignments</p>
          </div>
        </div>
        <button onClick={onCreate} className="btn-primary text-xs flex items-center space-x-1.5 shadow-sm">
          <Plus className="h-3.5 w-3.5" />
          <span>New Structure</span>
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-xs text-gray-500">Loading fee structures...</div>
      ) : structures.length === 0 ? (
        <div className="py-8 text-center text-gray-500 bg-gray-50 rounded border border-dashed border-gray-300">
          <p className="text-xs">No fee structures found.</p>
          <button onClick={onCreate} className="mt-1 text-xs hover:underline" style={{ color: 'var(--primary-color, #4f46e5)' }}>Create your first structure</button>
        </div>
      ) : (
        <div className="space-y-3">
          {structures.map(structure => (
            <FeeStructureItem 
              key={structure._id}
              structure={structure}
              allClasses={allClasses}
              formatCurrency={formatCurrency}
              onEdit={onEdit}
              onDelete={onDelete}
              onSendReminders={onSendReminders}
              sendingReminders={sendingReminders}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FeeStructureList;
